import {
	ContainerWithBusyIndicator,
	FormattedText,
	ToolbarAction,
	useConfirmationDialog
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IRootValidation,
	IValidationConfiguration
} from "client/Validation";
import { isNotNullNorUndefined } from "client/Validation/rules";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { SettingsPage } from "../SettingsPage";
import {
	get,
	ManageAdminRoleLocation,
	regenerate,
	save,
	UserProvisioningKind,
	UserProvisioningSettingsModel,
	UserProvisioningSettingsSaveModel,
	UserProvisioningSettingsViewModel
} from "client/Api/UserProvisioningSettings/userProvisioningSettings";
import { UserProvisioningForm } from "./UserProvisioningForm";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { useSidebarMenuStore } from "../SidebarMenu/sidebarMenuStore";
import { FetchCallback } from "client/zustand/createAutoFetchStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export const UserProvisioningPage = (): ReactElement => {
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [keyToReveal, setKeyToReveal] = useState<string | null>(null);
	const [data, setData] = useState<UserProvisioningSettingsSaveModel>({
		kind: UserProvisioningKind.AdSync,
		computerName: "",
		adSync: {
			isEnabled: false,
			canManageUserActiveStatusInWorkspace: false,
			manageAdminRoleLocation: ManageAdminRoleLocation.AzureAD
		},
		scim: {
			isEnabled: false,
			shouldActivateUserByDefault: false
		}
	});

	const [state, setState] = useState<UserProvisioningState>({
		initialData: defaultUserProvisioningSettings,
		shouldSpecifyComputerName: false,
		isScimProvisioningAvailable: false,
		isAdSyncAvailable: false,
		hasAuthenticationToken: false
	});

	const {
		initialData,
		shouldSpecifyComputerName,
		isScimProvisioningAvailable,
		isAdSyncAvailable,
		hasAuthenticationToken
	} = state;

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	useActiveSettingsMenuItem(SettingsPageKind.UserProvisioning);
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	const initializeSidebar: FetchCallback = useSidebarMenuStore(x => x.fetch);

	const [confirmationDialogElement, openConfirmationDialog] = useConfirmationDialog();

	const currentKind: UserProvisioningKind = data.kind;

	const formHandler: IFormHandler<UserProvisioningSettingsSaveModel> = useFormHandler<UserProvisioningSettingsSaveModel>(
		data,
		setData,
		validationConfiguration
	);

	const validationRef: RefObject<IRootValidation<UserProvisioningSettingsSaveModel>> = useRef<
		IRootValidation<UserProvisioningSettingsSaveModel>
	>(
		formHandler.validation
	);

	useEffect(
		() => {
			validationRef.current = formHandler.validation;
		},
		[formHandler.validation]
	);

	const regenerateTokenHandler = useCallback(
		async (): Promise<void> => {

			if (currentKind === UserProvisioningKind.None) {
				throw new Error("No user provisioning method is selected");
			}

			try {
				const {
					newlyGeneratedAuthenticationToken
				} = await regenerate(
					{ kind: currentKind },
					globalCancellation
				);

				if (!newlyGeneratedAuthenticationToken.isSet) {
					throw new Error("Authentication token is not regenerated.");
				}

				setKeyToReveal(newlyGeneratedAuthenticationToken.value);
			} catch (e) {
				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			alertManager,
			currentKind,
			globalCancellation
		]
	);

	const regenerateClickHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<void> = await openConfirmationDialog({
				title: localize("_AreYouSureYouWantToRegenerateTheApiKey"),
				children: (
					<FormattedText
						text={localize("_NoteTheCurrentApiKeyWillBeRevoked")}
						thingsToReplace={[
							{
								what: "{{Note}}",
								filler: <strong>{localize("_Note")}</strong>
							}
						]}
					/>
				),
				confirmText: localize("_Regenerate"),
				trackingId: "regenerate-api-key-confirmation-dialog"
			});

			if (result.wasSuccessful) {
				regenerateTokenHandler();
			}
		},
		[
			openConfirmationDialog,
			regenerateTokenHandler
		]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			const validationResult: InputValidationResult = await validationRef.current
				.validate();

			if (validationResult.kind === InputValidationResultKind.Invalid) {
				return;
			}

			try {
				setIsBusy(true);

				const formData: UserProvisioningSettingsSaveModel = prepareForSaving(data);

				const {
					newlyGeneratedAuthenticationToken
				} = await save(
					formData,
					globalCancellation
				);

				if (newlyGeneratedAuthenticationToken.isSet) {
					setKeyToReveal(
						newlyGeneratedAuthenticationToken
							.value
					);
				}

				setState(prevState => ({
					...prevState,
					initialData: data,
					hasAuthenticationToken: data.kind === prevState.initialData.kind
						&& prevState.hasAuthenticationToken === true
							|| newlyGeneratedAuthenticationToken.isSet
				}));

				alertManager.showSuccess(localize("_ChangesHaveAlreadySaved"));
				setIsBusy(false);
				initializeSidebar({ forceReload: true });
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_ChangesNotSaved"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			data,
			globalCancellation,
			initializeSidebar
		]
	);

	const cancelHandler = useCallback(
		() => {
			formHandler
				.reducer
				.setValue(initialData);
			validationRef
				.current
				.triggerInputFinished();
		},
		[
			formHandler.reducer,
			initialData
		]
	);

	const defaultActions: readonly ProvisioningActions[] = useMemo(
		() => listDefaultActions(
			saveHandler,
			cancelHandler
		),
		[
			saveHandler,
			cancelHandler
		]
	);

	const topBarActions = useMemo(
		(): readonly ProvisioningActions[] => {
			return buildActionsForTopBar<UserProvisioningSettingsSaveModel, ProvisioningActions>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	useEffect(
		() => {
			const fetchModels = async (signal: AbortSignal) => {
				try {
					setIsBusy(true);

					const models: UserProvisioningSettingsViewModel = await get(signal)
						.catch(handlePageDataLoadingError);

					const {
						model,
						computerName,
						isAdSyncAvailable,
						isScimProvisioningAvailable,
						shouldSpecifyComputerName
					} = models;

					const initialData: UserProvisioningSettingsSaveModel = mapToUserProvisioningSettingsSaveModel(
						model,
						computerName
					);

					setData(initialData);

					setState({
						initialData,
						isAdSyncAvailable,
						isScimProvisioningAvailable,
						shouldSpecifyComputerName,
						hasAuthenticationToken: model.kind === UserProvisioningKind.None
							? false
							: model.hasAuthenticationToken
					});

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			};

			const cancellation = new AbortController();
			fetchModels(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			globalCancellation
		]
	);

	return (
		<>
			<SettingsPage
				actions={topBarActions}
				actionContext={data}
				title={localize("_UserProvisioning")}
			>
				<SettingsPage.Section
					height="stretch"
				>
					<ContainerWithBusyIndicator
						hasBusyIndicator={isBusy}
						size="fill"
					>
						<UserProvisioningForm
							attachable={formHandler}
							initialKind={initialData.kind}
							hasAuthenticationToken={hasAuthenticationToken}
							isAdSyncAvailable={isAdSyncAvailable}
							isScimProvisioningAvailable={isScimProvisioningAvailable}
							shouldSpecifyComputerName={shouldSpecifyComputerName}
							keyToReveal={keyToReveal}
							onKeyToRevealChanged={setKeyToReveal}
							onRegenerateToken={regenerateClickHandler}
						/>
					</ContainerWithBusyIndicator>
				</SettingsPage.Section>
				<AiAssistantFabSpacing />
			</SettingsPage>
			{confirmationDialogElement}
		</>
	);
};
UserProvisioningPage.displayName = "UserProvisioningPage";

type UserProvisioningState = {
	initialData: UserProvisioningSettingsSaveModel;
	shouldSpecifyComputerName: boolean;
	isScimProvisioningAvailable: boolean;
	isAdSyncAvailable: boolean;
	hasAuthenticationToken: boolean;
};

const validationConfiguration: IValidationConfiguration<UserProvisioningSettingsSaveModel> =
	createValidationConfiguration<UserProvisioningSettingsSaveModel>();

validationConfiguration
	.when(
		model => model.kind === UserProvisioningKind.AdSync
	)
	.forScalar("adSync")
	.add(
		isNotNullNorUndefined()
	);

validationConfiguration
	.when(
		model => model.kind === UserProvisioningKind.Scim
	)
	.forScalar("scim")
	.add(
		isNotNullNorUndefined()
	);

const defaultUserProvisioningSettings: UserProvisioningSettingsSaveModel = {
	kind: UserProvisioningKind.None,
	computerName: "",
	adSync: {
		isEnabled: true,
		manageAdminRoleLocation: ManageAdminRoleLocation.AzureAD,
		canManageUserActiveStatusInWorkspace: false
	},
	scim: {
		isEnabled: true,
		shouldActivateUserByDefault: false
	}
};

function mapToUserProvisioningSettingsSaveModel(
	model: UserProvisioningSettingsModel,
	computerName: string
): UserProvisioningSettingsSaveModel {

	const result: UserProvisioningSettingsSaveModel = {
		...defaultUserProvisioningSettings,
		computerName
	};

	switch (model.kind) {
		case UserProvisioningKind.None:
			return result;
		case UserProvisioningKind.AdSync:
			const {
				isEnabled,
				manageAdminRoleLocation,
				canManageUserActiveStatusInWorkspace,
			} = model;

			return {
				...result,
				kind: UserProvisioningKind.AdSync,
				adSync: {
					isEnabled,
					manageAdminRoleLocation,
					canManageUserActiveStatusInWorkspace
				}
			};
		case UserProvisioningKind.Scim:
			return {
				...result,
				kind: UserProvisioningKind.Scim,
				scim: {
					isEnabled: model.isEnabled,
					shouldActivateUserByDefault: model.shouldActivateUserByDefault
				}
			};
		default:
			never(model);
	}
}

type ProvisioningActions = ToolbarAction<UserProvisioningSettingsSaveModel>;

function listDefaultActions(
	onSubmit: () => void,
	onCancel: () => void,
): readonly ProvisioningActions[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "save",
			title: localize("_Save"),
			canExecute: () => true,
			execute: () => onSubmit(),
			icon: "floppy-disk",
			isPrimary: true
		},
		{
			kind: ActionKind.Regular,
			name: "cancel",
			title: localize("_Cancel"),
			canExecute: () => true,
			execute: () => onCancel(),
			icon: "xmark"
		}
	];
}

function prepareForSaving (
	model: UserProvisioningSettingsSaveModel
): UserProvisioningSettingsSaveModel {
	const {
		computerName,
		kind,
		adSync,
		scim
	} = model;

	const result: UserProvisioningSettingsSaveModel = {
		kind,
		computerName
	};

	switch (kind) {
		case UserProvisioningKind.None:
			return result;
		case UserProvisioningKind.AdSync:
			return {
				...result,
				adSync
			};
		case UserProvisioningKind.Scim:
			return {
				...result,
				scim
			};
		default:
			never(kind);
	}
}

export const userProvisioningRouting = {
	page: "/UserProvisioningSettings"
};